import React from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Autocomplete,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  useCreateUserMutation,
  useGetOrganizationsQuickQuery,
} from 'src/common/apis/AdminApi';
import { useUserRole } from 'src/common/hooks/useUserRole';
import { UserRoles } from 'src/common/types/User';
import SnackbarUtils from 'src/common/utils/SnackbarUtilsConfigurator';

import { ReactComponent as CancelIconBlue } from '../../../../common/assets/icons/cancel_icon_blue.svg';
import { ReactComponent as SearchIcon } from '../../../../common/assets/icons/search_icon_small_gray.svg';
import OrganizationInput from '../../OrganizationCreateScreen/components/OrganizationInput';

type Props = {
  onClose: () => void;
};

const ROLE_OPTIONS: { label: string; value: UserRoles }[] = [
  { label: 'Provider', value: UserRoles.USER },
  { label: 'Coder', value: UserRoles.CODER },
  { label: 'Manager', value: UserRoles.ORG_OWNER },
  { label: 'Admin', value: UserRoles.SUPER_ADMIN },
];

const CreateUserPopup: React.FC<Props> = ({ onClose }) => {
  const { userRole } = useUserRole();

  const [email, setEmail] = React.useState<string>('');
  const [fullName, setFullName] = React.useState<string>('');
  const [role, setRole] = React.useState<UserRoles>(UserRoles.USER);
  const [selectedOrganization, setSelectedOrganization] =
    React.useState<string>('');
  const [password, setPassword] = React.useState<string>('');

  const [createdPassword, setCreatedPassword] = React.useState<string | null>(
    null
  );

  const { data: organizations, isFetching: orgsIsLoading } =
    useGetOrganizationsQuickQuery(undefined, {
      skip: userRole !== UserRoles.SUPER_ADMIN,
      refetchOnMountOrArgChange: true,
    });

  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();

  const copyPassword = () => {
    if (!createdPassword) return;
    navigator.clipboard
      .writeText(createdPassword)
      .then(() => SnackbarUtils.success('Password copied to clipboard'))
      .catch(() => SnackbarUtils.error('Failed to copy password'));
  };

  const orgRequired =
    role === UserRoles.ORG_OWNER || role === UserRoles.CODER;

  const submit = async () => {
    if (!email || !fullName) {
      SnackbarUtils.error('Email and Full Name are required.');
      return;
    }

    if (orgRequired && !selectedOrganization) {
      SnackbarUtils.error(
        'Select an organization — Managers and Coders must belong to one.'
      );
      return;
    }

    const organizationId = selectedOrganization
      ? organizations?.find((item) => item.name === selectedOrganization)?.id
      : undefined;

    if (selectedOrganization && !organizationId) {
      SnackbarUtils.error(
        'There has been an error with the organization. Please select it again.'
      );
      return;
    }

    createUser({
      email,
      fullName,
      role,
      organizationId,
      password: password || undefined,
    })
      .unwrap()
      .then((res) => {
        SnackbarUtils.success(res.message);
        setCreatedPassword(res.password);
      })
      .catch((err) => {
        SnackbarUtils.error(err?.data?.message || 'Failed to create the user.');
      });
  };

  return (
    <Box
      sx={{
        maxWidth: '456px',
        minHeight: '240px',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flex: 1,
        borderRadius: '15px',
        padding: '40px',
        flexDirection: 'column',
      }}
    >
      <Box className='row' sx={{ alignItems: 'center' }}>
        <IconButton
          onClick={onClose}
          sx={{ alignSelf: 'flex-start', justifySelf: 'right' }}
        >
          <CancelIconBlue />
        </IconButton>
        <Typography
          sx={{
            alignSelf: 'center',
            whiteSpace: 'break-spaces',
            textAlign: 'center',
            mb: '24px',
            ml: 'auto',
            mr: 'auto',
          }}
          color='text.primary'
          variant='h5'
        >
          Create User
        </Typography>
        <Box sx={{ mr: 'auto' }} />
      </Box>

      {createdPassword ? (
        <Box>
          <Typography
            sx={{ textAlign: 'center', mb: '16px' }}
            color='text.secondary'
          >
            User created successfully. Copy this password now — it won&apos;t be
            shown again.
          </Typography>
          <TextField
            fullWidth
            value={createdPassword}
            label='Password'
            InputProps={{ readOnly: true }}
            sx={{ [`& fieldset`]: { borderRadius: '50px' } }}
          />
          <Box
            sx={{
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: 'center',
              display: 'flex',
              marginTop: '20px',
            }}
          >
            <Button
              onClick={copyPassword}
              disableElevation
              color='primary'
              variant='contained'
              sx={{
                borderRadius: '50px',
                width: '180px',
                height: '60px',
                marginRight: '15px',
              }}
            >
              <Typography color='white'>Copy</Typography>
            </Button>
            <Button
              onClick={onClose}
              disableElevation
              color='appGray2'
              variant='contained'
              sx={{
                borderRadius: '50px',
                width: '180px',
                height: '60px',
              }}
            >
              <Typography color='white'>Close</Typography>
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Box>
            <OrganizationInput
              title='Email'
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Box>
          <Box style={{ marginTop: '24px' }}>
            <OrganizationInput
              title='Full Name'
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
            />
          </Box>
          <Box style={{ marginTop: '24px' }}>
            <OrganizationInput
              title='Role'
              select
              value={role}
              onChange={(event) => setRole(event.target.value as UserRoles)}
            >
              {ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </OrganizationInput>
          </Box>
          <Box style={{ marginTop: '24px' }}>
            <Autocomplete
              disabled={userRole !== UserRoles.SUPER_ADMIN}
              id='create-user-organization-autocomplete'
              options={
                organizations ? organizations.map((item) => item.name) : []
              }
              onChange={(_event, newValue) =>
                setSelectedOrganization(newValue ?? '')
              }
              loading={orgsIsLoading || isCreating}
              getOptionLabel={(option) => option}
              renderInput={(params) => (
                <OrganizationInput
                  {...params}
                  title={orgRequired ? 'Organization' : 'Organization (optional)'}
                  startIcon={
                    userRole !== UserRoles.SUPER_ADMIN ? <></> : <SearchIcon />
                  }
                  disabled={orgsIsLoading || isCreating}
                />
              )}
            />
          </Box>
          <Box style={{ marginTop: '24px' }}>
            <OrganizationInput
              title='Password'
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ mt: '8px', ml: '4px' }}
            >
              Leave blank to auto-generate a secure password.
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: 'center',
              display: 'flex',
              marginTop: '20px',
            }}
          >
            <Button
              onClick={onClose}
              disableElevation
              color='appGray2'
              variant='contained'
              sx={{
                borderRadius: '50px',
                width: '180px',
                height: '60px',
                marginRight: '15px',
              }}
            >
              <Typography color='white'>Cancel</Typography>
            </Button>
            <LoadingButton
              onClick={submit}
              disabled={isCreating || !email || !fullName}
              loading={isCreating}
              disableElevation
              color='primary'
              variant='contained'
              sx={{
                borderRadius: '50px',
                width: '180px',
                height: '60px',
              }}
            >
              <Typography color='white'>Ok</Typography>
            </LoadingButton>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CreateUserPopup;
